import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import { clearSessionActivity } from './session_activity';
import { clearAuthUser, getAuthUserString } from './auth_storage';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_WORDPRESS_GRAPHQL_URL || 'https://ngktours.com/graphql',
  credentials: 'omit',
  fetchOptions: {
    // WooCommerce 302s failed logins to /my-account/?login=failed. Never follow that.
    redirect: 'error',
  },
});

const AUTH_OPERATION_NAMES = new Set([
  'Login',
  'PortalLogin',
  'VerifyEmailOtp',
  'ResendEmailOtp',
  'VerifyTotp',
  'BeginTotpEnrollment',
  'ConfirmTotpEnrollment',
  'ResetUserTotp',
  'DisableOwnTotp',
  'SetOwnEmailOtpPreference',
  'RefreshJwtAuthToken',
  'SendPasswordResetEmail',
  'ResetUserPassword',
]);

const authLink = setContext((operation, { headers }) => {
  // Do not send a leftover JWT on login/OTP — invalid Bearer tokens often
  // return 401 without CORS headers, which the browser reports as "Failed to fetch".
  if (AUTH_OPERATION_NAMES.has(operation.operationName || '')) {
    const nextHeaders = { ...headers };
    delete (nextHeaders as any).authorization;
    delete (nextHeaders as any).Authorization;
    return { headers: nextHeaders };
  }

  const authUser = getAuthUserString();
  const token = authUser ? JSON.parse(authUser).accessToken : null;

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

let handlingAuthError = false;

function isRealAuthError(message: string, debugMessage: string, code: string): boolean {
  const text = `${message} ${debugMessage} ${code}`.toLowerCase();

  // Do NOT treat generic "internal server error" as auth — that caused stuck dashboards.
  return (
    (text.includes('unauthorized') && !text.includes('access this order')) ||
    text.includes('jwt expired') ||
    text.includes('expired token') ||
    text.includes('invalid token') ||
    text.includes('token expired') ||
    text.includes('token is invalid') ||
    text.includes('not logged in') ||
    text.includes('login required') ||
    text.includes('authentication failed') ||
    text.includes('invalid credentials') ||
    code === '401' ||
    code === 'unauthorized' ||
    code === 'invalid_token' ||
    code === 'jwt_auth_invalid_token'
  );
}

function forceLogout(reason: string) {
  console.log('❌ Session ended:', reason);
  handlingAuthError = false;
  clearAuthUser();
  clearSessionActivity();
  import('./auth_relogin')
    .then(({ clearStoredCredentials }) => clearStoredCredentials())
    .catch(() => undefined);

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

const errorLink = onError(({ graphQLErrors, networkError, operation }: any) => {
  const operationName = operation?.operationName || 'Unknown';
  if (AUTH_OPERATION_NAMES.has(operationName) || handlingAuthError) {
    return;
  }

  let isAuthError = false;

  if (graphQLErrors) {
    graphQLErrors.forEach((error: GraphQLError) => {
      const message = error.message || '';
      const debugMsg = (error.extensions?.debugMessage as string) || '';
      const errorCode = error.extensions?.code?.toString() || '';
      if (isRealAuthError(message, debugMsg, errorCode)) {
        isAuthError = true;
      }
    });
  }

  if (networkError) {
    const statusCode = (networkError as any)?.statusCode;
    const errorMsg = networkError.message?.toLowerCase() || '';
    if (
      statusCode === 401 ||
      statusCode === 403 ||
      errorMsg.includes('jwt') ||
      (errorMsg.includes('unauthorized') && !errorMsg.includes('access this order'))
    ) {
      isAuthError = true;
    }
  }

  if (!isAuthError) {
    return;
  }

  handlingAuthError = true;

  (async () => {
    try {
      const { attemptSilentRelogin } = await import('./auth_relogin');
      const ok = await attemptSilentRelogin();
      if (ok) {
        // Reload so all queries run again with the fresh token.
        window.location.reload();
        return;
      }
    } catch (error) {
      console.error('Auth recovery failed:', error);
    }
    forceLogout('token expired and re-auth failed');
  })();
});

export const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
});
